import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Star, Eye, X, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import OpportunityBadge from "@/components/OpportunityBadge";
import ScoreBadge from "@/components/ScoreBadge";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { fmtPct } from "@/lib/format";
import SignalActionBar from "@/components/SignalActionBar";

/**
 * Trade of the Day — v2.6 hero card. One curated pick per UTC day.
 * Signal Copilot direction (D): curated, mobile-first, no execution.
 */
function formatLocalToday() {
  return new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric" }).format(new Date());
}

function formatPickedAt(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(d);
}

export default function TradeOfTheDay({ testid = "trade-of-day" }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["trade-of-day"],
    queryFn: async () => (await api.get("/copilot/trade-of-day")).data,
    staleTime: 60_000,
    // Poll every 2 min so server-side re-syncs (rating drift, better-signal
    // swaps) flow to the UI without a manual refresh.
    refetchInterval: 120_000,
  });

  const refreshMut = useMutation({
    mutationFn: async () => (await api.post("/copilot/trade-of-day/refresh")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trade-of-day"] });
      toast.success("Trade of the Day updated");
    },
  });

  if (isLoading) {
    return (
      <div className="border border-zinc-800 rounded-md p-4 text-sm text-zinc-500 flex items-center gap-2"
           data-testid={testid}>
        <Loader2 size={14} className="animate-spin" /> Choosing today&apos;s pick…
      </div>
    );
  }
  if (!data || !data.pick) {
    return (
      <div className="border border-zinc-800 rounded-md p-4" data-testid={testid}>
        <div className="flex items-center gap-2 text-sm text-zinc-400">
          <Star size={14} className="text-amber-300" />
          <span>No Trade of the Day yet — no qualifying active signal.</span>
          <button
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending}
            data-testid={`${testid}-refresh`}
            className="ml-auto text-xs text-zinc-300 hover:text-zinc-100 underline"
          >
            {refreshMut.isPending ? "Refreshing…" : "Try again"}
          </button>
        </div>
      </div>
    );
  }

  const pick = data.pick;
  const signal = data.signal || {};
  const userAction = data.user_action;
  const localToday = formatLocalToday();
  const pickedAt = formatPickedAt(pick.chosen_at);

  return (
    <section
      className="relative overflow-hidden rounded-md border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] via-zinc-950 to-zinc-950 p-4 md:p-6"
      data-testid={testid}
    >
      {/* Marker */}
      <div className="flex items-center gap-2">
        <Star size={13} className="text-amber-300" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
          Trade of the Day · {localToday}
          {pickedAt && <span className="ml-2 text-zinc-500 normal-case tracking-normal">picked {pickedAt}</span>}
        </span>
      </div>

      {/* Headline */}
      <Link
        to={`/app/signals/${pick.signal_id}`}
        className="block mt-2"
        data-testid={`${testid}-headline`}
      >
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-zinc-100 tracking-tight leading-tight hover:underline">
          {pick.market_title}
        </h2>
      </Link>

      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <OpportunityBadge rating={pick.opportunity_rating} size="lg" />
        <ScoreBadge score={pick.signal_score} size="lg" />
        <ConfidenceBadge confidence={pick.confidence} />
        <span className="text-[11px] text-zinc-500 ml-1">
          direction: <span className="text-zinc-200">{pick.outcome}</span>
        </span>
        {signal.current_probability != null && (
          <span className="text-[11px] text-zinc-500 ml-1">
            · at <span className="text-zinc-200 font-mono-num">{fmtPct(signal.current_probability, 0)}</span>
          </span>
        )}
      </div>

      {/* Rationale */}
      <p className="mt-3 text-sm text-zinc-300 leading-relaxed max-w-3xl">
        {pick.rationale}
      </p>

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <SignalActionBar
          signalId={pick.signal_id}
          currentAction={userAction}
          testid={`${testid}-actions`}
          onChange={() => qc.invalidateQueries({ queryKey: ["trade-of-day"] })}
        />
        <Link
          to={`/app/signals/${pick.signal_id}`}
          className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-zinc-100 ml-auto"
          data-testid={`${testid}-open`}
        >
          Open signal <ArrowRight size={12} />
        </Link>
      </div>
    </section>
  );
}
